/**
 * Règles de famille COF2 - bonus applicables au personnage selon sa famille.
 *
 * Les 4 familles (aventurier, combattant, mage, mystique) donnent chacune :
 * - un PV de base par niveau
 * - un dé de récupération (DR)
 * - un bonus particulier (cf. table)
 *
 * Source : `regles.json -> creation_personnage.familles` + `profils.json -> familles`.
 */

export type FamilleId = 'aventuriers' | 'combattants' | 'mages' | 'mystiques';

/**
 * Tableau des 4 familles avec leurs paramètres consolidés.
 *
 * Valeurs extraites du livre de règles, **ne pas modifier sans relecture**.
 */
export interface FamilleStats {
  readonly id: FamilleId;
  readonly label: string;
  /** PV ajoutés à chaque niveau (et doublés au niveau 1). */
  readonly pvPerLevel: number;
  /** Type de dé de récupération (ex: d8, d10, d6). */
  readonly dr: 'd6' | 'd8' | 'd10';
  /** +1 PC de chance en bonus (aventurier seulement). */
  readonly bonusPC: number;
  /** +1 DR en bonus (mystique seulement). */
  readonly bonusDR: number;
  /** Le mage gagne une capacité supplémentaire à la création. */
  readonly bonusMage: boolean;
}

export const FAMILLES: Readonly<Record<FamilleId, FamilleStats>> = {
  aventuriers: {
    id: 'aventuriers',
    label: 'Aventurier',
    pvPerLevel: 4,
    dr: 'd8',
    bonusPC: 1,
    bonusDR: 0,
    bonusMage: false,
  },
  combattants: {
    id: 'combattants',
    label: 'Combattant',
    pvPerLevel: 5,
    dr: 'd10',
    bonusPC: 0,
    bonusDR: 0,
    bonusMage: false,
  },
  mages: {
    id: 'mages',
    label: 'Mage',
    pvPerLevel: 3,
    dr: 'd6',
    bonusPC: 0,
    bonusDR: 0,
    bonusMage: true,
  },
  mystiques: {
    id: 'mystiques',
    label: 'Mystique',
    pvPerLevel: 4,
    dr: 'd8',
    bonusPC: 0,
    bonusDR: 1,
    bonusMage: false,
  },
};

export function getFamille(id: FamilleId): FamilleStats {
  return FAMILLES[id];
}

/**
 * Tests si une chaîne est un FamilleId valide.
 */
export function isFamilleId(v: unknown): v is FamilleId {
  return typeof v === 'string' && v in FAMILLES;
}

/**
 * Formule PV niveau 1 : 2 x PV_famille + CON.
 * Exemple : aventurier CON+2 -> 2x4 + 2 = 10 PV.
 */
export function pvAtLevel1(famille: FamilleId, con: number): number {
  return 2 * FAMILLES[famille].pvPerLevel + con;
}

/**
 * Formule PC Chance : 2 + CHA, + 1 si aventurier.
 */
export function pcBase(famille: FamilleId, cha: number): number {
  const base = 2 + cha + FAMILLES[famille].bonusPC;
  return Math.max(0, base);
}

/**
 * Formule nombre de dés de récupération : 2 + CON, + 1 si mystique.
 */
export function drCount(famille: FamilleId, con: number): number {
  const base = 2 + con + FAMILLES[famille].bonusDR;
  return Math.max(0, base);
}
